import React from 'react';
import { useDropzone } from 'react-dropzone';
import {
	Box,
	Typography,
	Grid,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

import Input from '../../../common/form/Input';
import MaskInput from '../../../common/form/MaskInput';
import Button from '../../../common/buttons/Button';
import RadioButtons from '../../../common/buttons/RadioButtons';
import { FormValues } from './types';
import Select from '../../../common/form/Select';
import { shirtSizeOptions, shoeSizeOptions } from '../internal/constants';
import { CrewRolesDDOptions } from '../../../../data/models/user';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../../data/stores/alertStore';
import * as GoogleStorageService from '../../../../services/google-storage.service';

interface ProfessionalDetailsStepProps {
	formValues: FormValues;
	setValue: any;
	control: any;
	errors: any;
	isRequired?: boolean;
	token?: string;
	onDropResume?: (acceptedFiles: File[]) => void;
	onDropKit?: (acceptedFiles: File[]) => void;
	onDocumentsUpdate?: (payload: Partial<FormValues>) => Promise<void> | void;
}

export default function ProfessionalDetailsStep({
	formValues,
	setValue,
	control,
	errors,
	isRequired = true,
	token,
	onDropResume,
	onDropKit,
	onDocumentsUpdate,
}: ProfessionalDetailsStepProps) {
	const dispatch = useDispatch();
	const {
		getRootProps: getResumeRootProps,
		getInputProps: getResumeInputProps,
	} = useDropzone({
		onDrop: onDropResume,
		maxFiles: 5,
		maxSize: 5 * 1024 * 1024,
	});
	const { getRootProps: getKitRootProps, getInputProps: getKitInputProps } =
		useDropzone({
			onDrop: onDropKit,
			maxFiles: 5, // Allow multiple files for kit documents
			maxSize: 5 * 1024 * 1024,
		});

	const renderUploadDropzone = (
		label: string,
		getRootProps: any,
		getInputProps: any,
		files: File[],
		documents: any[],
		onRemoveFile: (index: number) => void,
		onRemoveDocument: (index: number) => void,
		maxFiles: number = 5
	) => {
		const totalItems = files.length + documents.length;

		return (
			<Box sx={{ mb: 3 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					{label}
				</Typography>
				{totalItems < maxFiles && (
					<Box
						{...getRootProps()}
						sx={{
							border: '2px dashed',
							borderColor: 'divider',
							borderRadius: '12px',
							p: 2,
							backgroundColor: '#2F2F2F',
							cursor: 'pointer',
						}}
					>
						<input {...getInputProps()} />
						<Typography variant="body2" color="primary">
							Click to upload or drag and drop
						</Typography>
						<Typography
							variant="caption"
							sx={{ color: 'text.secondary' }}
						>
							SVG, PNG or JPG (max. 5MB)
						</Typography>
					</Box>
				)}

				{/* Render uploaded files (pending upload) */}
				{files.map((file, index) => (
					<Box
						key={`file-${index}`}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							mt: 1.5,
							p: 1,
							borderRadius: '8px',
							backgroundColor: 'rgba(255,255,255,0.04)',
						}}
					>
						<InsertDriveFileOutlinedIcon fontSize="small" />
						<Typography variant="body2" sx={{ flex: 1 }}>
							{file.name}
						</Typography>
						<IconButton
							size="small"
							onClick={() => onRemoveFile(index)}
							aria-label="remove"
						>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
					</Box>
				))}

				{/* Render uploaded documents (already uploaded) */}
				{documents.map((doc, index) => (
					<Box
						key={`doc-${index}`}
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							mt: 1.5,
							p: 1,
							borderRadius: '8px',
							backgroundColor: 'rgba(255,255,255,0.04)',
						}}
					>
						<InsertDriveFileOutlinedIcon fontSize="small" />
						<Typography variant="body2" sx={{ flex: 1 }}>
							{doc.name ||
								doc.publicUrl?.split('/').pop() ||
								`Document ${index + 1}`}
						</Typography>
						<IconButton
							size="small"
							onClick={() => onRemoveDocument(index)}
							aria-label="remove"
						>
							<DeleteOutlineIcon fontSize="small" />
						</IconButton>
						<CheckCircleRoundedIcon
							color="success"
							fontSize="small"
						/>
					</Box>
				))}
			</Box>
		);
	};

	const hasKitYesNo = formValues.hasKit ? 'yes' : 'no';
	const willPhotoYesNo = formValues.willingToBePhotographedOnSet
		? 'yes'
		: 'no';

	return (
		<Box sx={{ py: 2 }}>
			{/* Work Samples and Social */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Work Samples and Social
			</Typography>
			<Grid container spacing={3} sx={{ mb: 2 }}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="websiteUrl"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Website / portfolio link"
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="instagramHandle"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								label="Instagram handle"
								fullWidth
							/>
						)}
					/>
				</Grid>
			</Grid>
			{renderUploadDropzone(
				'Resume and Project History upload',
				getResumeRootProps,
				getResumeInputProps,
				[], // No pending files since we upload immediately
				formValues.resumeProjectHistoryUploads || [],
				() => {}, // No pending files to remove
				async (index: number) => {
					const currentUploads =
						formValues.resumeProjectHistoryUploads || [];
					const documentToDelete = currentUploads[index];

					if (documentToDelete) {
						try {
							// Delete from Google Storage
							await GoogleStorageService.deleteFile(
								documentToDelete.publicUrl ||
									documentToDelete.url,
								token
							);

							// Remove from local state
							const newUploads = currentUploads.filter(
								(_, i) => i !== index
							);
							setValue(
								'resumeProjectHistoryUploads',
								newUploads,
								{
									shouldDirty: true,
								}
							);
							if (onDocumentsUpdate) {
								try {
									await onDocumentsUpdate({
										resumeProjectHistoryUploads: newUploads,
									});
								} catch (saveError) {
									console.error(
										'Error saving resume document updates:',
										saveError
									);
									setValue(
										'resumeProjectHistoryUploads',
										currentUploads,
										{
											shouldDirty: true,
										}
									);
									dispatch(
										showAlert({
											message:
												'Error saving resume document updates',
											severity: 'error',
										})
									);
									return;
								}
							}

							dispatch(
								showAlert({
									message:
										'Resume document deleted successfully',
									severity: 'success',
								})
							);
						} catch (error) {
							console.error(
								'Error deleting resume document:',
								error
							);
							dispatch(
								showAlert({
									message: 'Error deleting resume document',
									severity: 'error',
								})
							);
						}
					}
				}
			)}

			{/* Work History & Status */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Work History & Status
			</Typography>

			<Box sx={{ mb: 3 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					Current job status
				</Typography>
				<Controller
					name="currentJobStatus"
					control={control}
					render={({ field }) => (
						<RadioButtons
							label=""
							name="currentJobStatus"
							options={[
								{
									value: 'full_time_freelance',
									label: 'Full time freelance with availability and seeking additional freelance work',
								},
								{
									value: 'full_time_employed_seeking_side_work',
									label: 'Full time employed seeking freelance work on the side',
								},
								{
									value: 'full_time_employed_not_seeking',
									label: 'Full time employed currently not seeking freelance work',
								},
							]}
							value={field.value}
							onChange={field.onChange}
						/>
					)}
				/>
			</Box>

			<Box sx={{ mb: 3 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					I want to be contacted about upcoming freelance work
				</Typography>
				<Controller
					name="wantFreelanceContact"
					control={control}
					render={({ field }) => (
						<RadioButtons
							label=""
							name="wantFreelanceContact"
							options={[
								{ value: 'yes', label: 'Yes' },
								{ value: 'no', label: 'No' },
							]}
							value={field.value}
							onChange={field.onChange}
						/>
					)}
				/>
			</Box>

			<Box sx={{ mb: 3 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					I am interested in being contacted about full time w2 staff
					positions in house with Prod Co/Producer
				</Typography>
				<Controller
					name="interestedInW2Positions"
					control={control}
					render={({ field }) => (
						<RadioButtons
							label=""
							name="interestedInW2Positions"
							options={[
								{ value: 'yes', label: 'Yes' },
								{ value: 'no', label: 'No' },
							]}
							value={field.value}
							onChange={field.onChange}
						/>
					)}
				/>
			</Box>

			<Box sx={{ mb: 4 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					I am interested in a full time 3 month internship/residency
					program
				</Typography>
				<Controller
					name="interestedInInternship"
					control={control}
					render={({ field }) => (
						<RadioButtons
							label=""
							name="interestedInInternship"
							options={[
								{ value: 'yes', label: 'Yes' },
								{ value: 'no', label: 'No' },
								{ value: 'na', label: 'N/A' },
							]}
							value={field.value}
							onChange={field.onChange}
						/>
					)}
				/>
			</Box>

			<Controller
				name="unionStatus"
				control={control}
				rules={
					isRequired ? { required: 'Union status is required' } : {}
				}
				render={({ field, fieldState }) => (
					<RadioButtons
						label="Union Status"
						name="unionStatus"
						options={[
							{ value: 'UNION', label: 'Union' },
							{ value: 'NON_UNION', label: 'Non-Union' },
						]}
						value={field.value}
						onChange={field.onChange}
						required={isRequired}
						error={!!fieldState.error}
						helperText={fieldState.error?.message}
					/>
				)}
			/>
			{formValues.unionStatus === 'UNION' && (
				<Box sx={{ mb: 4 }}>
					<Typography variant="body2" sx={{ mb: 1 }}>
						If yes, what union?
					</Typography>
					<Controller
						name="unionName"
						control={control}
						render={({ field }) => (
							<Input
								{...field}
								fullWidth
								placeholder="Enter union name"
							/>
						)}
					/>
				</Box>
			)}

			{/* Primary Production Role */}
			<Accordion
				defaultExpanded
				sx={{
					mb: 4,
					backgroundColor: '#2F2F2F',
					boxShadow: 'none',
					borderRadius: '16px',
					borderBottomLeftRadius: '8px !important',
					borderBottomRightRadius: '8px !important',
					'&:before': { display: 'none' },
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					sx={{
						backgroundColor: '#2F2F2F',
						'& .MuiAccordionSummary-content': { margin: '12px 0' },
						borderTopLeftRadius: '8px',
						borderTopRightRadius: '8px',
					}}
				>
					<Typography variant="h6" sx={{ color: 'text.label' }}>
						Primary Production Role
					</Typography>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						backgroundColor: '#2F2F2F',
						borderBottomLeftRadius: '8px',
						borderBottomRightRadius: '8px',
					}}
				>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<Controller
								name="productionRole"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Primary role is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<Select
										{...field}
										label="Primary role"
										options={CrewRolesDDOptions}
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="rate"
								control={control}
								rules={
									isRequired
										? { required: 'Role rate is required' }
										: {}
								}
								render={({ field, fieldState }) => (
									<Input
										{...field}
										label="Role rate"
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="yearsOfExperiencePrimary"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Years of experience is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<RadioButtons
										label="Years of experience"
										name="yearsOfExperiencePrimary"
										options={[
											{
												value: '1-5 year',
												label: '1-5 year',
											},
											{
												value: '5-10 years',
												label: '5-10 years',
											},
											{
												value: '10-20 years',
												label: '10-20 years',
											},
											{
												value: '20+ years',
												label: '20+ years',
											},
										]}
										value={field.value}
										onChange={field.onChange}
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="rateType"
								control={control}
								rules={
									isRequired
										? { required: 'Rate type is required' }
										: {}
								}
								render={({ field, fieldState }) => (
									<RadioButtons
										label="Rate type"
										name="rateType"
										options={[
											{
												value: 'HOURLY',
												label: 'Hourly',
											},
											{ value: 'DAY', label: 'Day' },
											{
												value: 'WEEKLY',
												label: 'Weekly',
											},
											{
												value: 'MONTHLY',
												label: 'Monthly',
											},
										]}
										value={field.value}
										onChange={field.onChange}
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="rateTenOrTwelveHours"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Please specify if this rate is for 10 or 12 hour days',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<RadioButtons
										label="Is this rate for 10 or 12 hour days?"
										name="rateTenOrTwelveHours"
										options={[
											{
												value: '10 hour',
												label: '10 hour',
											},
											{
												value: '12 hour',
												label: '12 hour',
											},
										]}
										value={field.value}
										onChange={field.onChange}
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="isRateFixed"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Please specify if this rate is fixed or flexible',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<RadioButtons
										label="Is this rate fixed or flexible?"
										name="isRateFixed"
										options={[
											{ value: 'Fixed', label: 'Fixed' },
											{
												value: 'Flexible',
												label: 'Flexible',
											},
										]}
										value={
											field.value === true
												? 'Fixed'
												: field.value === false
												? 'Flexible'
												: ''
										}
										onChange={(e: any) =>
											field.onChange(
												e.target.value === 'Fixed'
											)
										}
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="chargesOvertimeTypically"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Please specify if you typically charge overtime',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<RadioButtons
										label="Do you typically charge overtime?"
										name="chargesOvertimeTypically"
										options={[
											{ value: 'Yes', label: 'Yes' },
											{ value: 'No', label: 'No' },
										]}
										value={
											field.value === true
												? 'Yes'
												: field.value === false
												? 'No'
												: ''
										}
										onChange={(e: any) =>
											field.onChange(
												e.target.value === 'Yes'
											)
										}
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<Controller
								name="workReferenceFirstName"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Industry work reference 1 first name is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<Input
										{...field}
										label="Industry work reference 1 first name"
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReferenceLastName"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Industry work reference 1 last name is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<Input
										{...field}
										label="Industry work reference 1 last name"
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReferenceEmail"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Industry work reference 1 email is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<Input
										{...field}
										label="Industry work reference 1 email"
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReferencePhone"
								control={control}
								rules={
									isRequired
										? {
												required:
													'Industry work reference 1 cell phone number is required',
										  }
										: {}
								}
								render={({ field, fieldState }) => (
									<MaskInput
										{...field}
										id="workReferencePhone"
										name="workReferencePhone"
										label="Industry work reference 1 cell phone #"
										mask="phone"
										fullWidth
										required={isRequired}
										error={!!fieldState.error}
										helperText={fieldState.error?.message}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<Controller
								name="workReference2FirstName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 first name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReference2LastName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 lastname"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReference2Email"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 email"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="workReference2Phone"
								control={control}
								render={({ field }) => (
									<MaskInput
										{...field}
										id="workReference2Phone"
										name="workReference2Phone"
										label="Industry work reference 2 cell phone #"
										mask="phone"
										fullWidth
									/>
								)}
							/>
						</Grid>

						{/* Kit Section - moved from bottom */}
						<Grid item xs={12}>
							<Box sx={{ mt: 2 }}>
								<Controller
									name="hasKit"
									control={control}
									render={({ field }) => (
										<RadioButtons
											label="Do you have a kit?"
											name="hasKit"
											options={[
												{ value: 'Yes', label: 'Yes' },
												{ value: 'No', label: 'No' },
											]}
											value={
												field.value === true
													? 'Yes'
													: field.value === false
													? 'No'
													: ''
											}
											onChange={(e: any) =>
												field.onChange(
													e.target.value === 'Yes'
												)
											}
										/>
									)}
								/>
							</Box>
						</Grid>

						{formValues.hasKit && (
							<>
								<Grid item xs={12}>
									<Controller
										name="kitContents"
										control={control}
										render={({ field }) => (
											<Input
												{...field}
												label="If yes, what is included in your kit?"
												multiline
												rows={4}
												fullWidth
												placeholder="Please describe what's included in your kit..."
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12} sm={6}>
									<Controller
										name="kitFee"
										control={control}
										render={({ field }) => (
											<Input
												{...field}
												label="Kit fee (if applicable)"
												fullWidth
											/>
										)}
									/>
								</Grid>
								<Grid item xs={12}>
									{renderUploadDropzone(
										'Upload kit document',
										getKitRootProps,
										getKitInputProps,
										[], // No pending files since we upload immediately
										formValues.kitDocuments || [],
										() => {}, // No pending files to remove
										async (index: number) => {
											const currentDocs =
												formValues.kitDocuments || [];
											const documentToDelete =
												currentDocs[index];

											if (documentToDelete) {
												try {
													// Delete from Google Storage
													await GoogleStorageService.deleteFile(
														documentToDelete.publicUrl ||
															documentToDelete.url,
														token
													);

													// Remove from local state
													const newDocs =
														currentDocs.filter(
															(_, i) =>
																i !== index
														);
													setValue(
														'kitDocuments',
														newDocs,
														{
															shouldDirty: true,
														}
													);

													if (onDocumentsUpdate) {
														try {
															await onDocumentsUpdate(
																{
																	kitDocuments:
																		newDocs,
																}
															);
														} catch (saveError) {
															console.error(
																'Error saving kit document updates:',
																saveError
															);
															setValue(
																'kitDocuments',
																currentDocs,
																{
																	shouldDirty:
																		true,
																}
															);
															dispatch(
																showAlert({
																	message:
																		'Error saving kit document updates',
																	severity:
																		'error',
																})
															);
															return;
														}
													}

													dispatch(
														showAlert({
															message:
																'Kit document deleted successfully',
															severity: 'success',
														})
													);
												} catch (error) {
													console.error(
														'Error deleting kit document:',
														error
													);
													dispatch(
														showAlert({
															message:
																'Error deleting kit document',
															severity: 'error',
														})
													);
												}
											}
										}
									)}
								</Grid>
							</>
						)}
					</Grid>
				</AccordionDetails>
			</Accordion>

			{/* Secondary Production Role */}
			<Accordion
				defaultExpanded
				sx={{
					mb: 4,
					backgroundColor: '#2F2F2F',
					boxShadow: 'none',
					borderRadius: '16px',
					borderBottomLeftRadius: '8px !important',
					borderBottomRightRadius: '8px !important',
					'&:before': { display: 'none' },
				}}
			>
				<AccordionSummary
					expandIcon={<ExpandMoreIcon />}
					sx={{
						backgroundColor: '#2F2F2F',
						'& .MuiAccordionSummary-content': { margin: '12px 0' },
						borderTopLeftRadius: '8px',
						borderTopRightRadius: '8px',
					}}
				>
					<Typography variant="h6" sx={{ color: 'text.label' }}>
						Secondary Production Role
					</Typography>
				</AccordionSummary>
				<AccordionDetails
					sx={{
						backgroundColor: '#2F2F2F',
						borderBottomLeftRadius: '8px',
						borderBottomRightRadius: '8px',
					}}
				>
					<Grid container spacing={3}>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryProductionRole"
								control={control}
								render={({ field }) => (
									<Select
										{...field}
										label="Secondary role"
										options={CrewRolesDDOptions}
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryRate"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Role rate"
										fullWidth
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="secondaryYearsOfExperience"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="Years of experience"
										name="secondaryYearsOfExperience"
										options={[
											{
												value: '1-5 year',
												label: '1-5 year',
											},
											{
												value: '5-10 years',
												label: '5-10 years',
											},
											{
												value: '10-20 years',
												label: '10-20 years',
											},
											{
												value: '20+ years',
												label: '20+ years',
											},
										]}
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="secondaryRateType"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="Rate Type"
										name="secondaryRateType"
										options={[
											{
												value: 'Hourly Rate',
												label: 'Hourly Rate',
											},
											{
												value: 'Day Rate',
												label: 'Day Rate',
											},
											{
												value: 'Weekly Rate',
												label: 'Weekly Rate',
											},
											{
												value: 'Monthly Rate',
												label: 'Monthly Rate',
											},
										]}
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="secondaryRateTenOrTwelveHours"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="Is this rate for 10 or 12 hour days?"
										name="secondaryRateTenOrTwelveHours"
										options={[
											{
												value: '10 hour',
												label: '10 hour',
											},
											{
												value: '12 hour',
												label: '12 hour',
											},
										]}
										value={field.value}
										onChange={field.onChange}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="secondaryIsRateFixed"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="Is this rate fixed or flexible?"
										name="secondaryIsRateFixed"
										options={[
											{ value: 'Fixed', label: 'Fixed' },
											{
												value: 'Flexible',
												label: 'Flexible',
											},
										]}
										value={
											field.value === true
												? 'Fixed'
												: field.value === false
												? 'Flexible'
												: ''
										}
										onChange={(e: any) =>
											field.onChange(
												e.target.value === 'Fixed'
											)
										}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12}>
							<Controller
								name="secondaryChargesOvertimeTypically"
								control={control}
								render={({ field }) => (
									<RadioButtons
										label="Do you typically charge overtime?"
										name="secondaryChargesOvertimeTypically"
										options={[
											{ value: 'Yes', label: 'Yes' },
											{ value: 'No', label: 'No' },
										]}
										value={
											field.value === true
												? 'Yes'
												: field.value === false
												? 'No'
												: ''
										}
										onChange={(e: any) =>
											field.onChange(
												e.target.value === 'Yes'
											)
										}
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReferenceFirstName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 1 first name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReferenceLastName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 1 last name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReferenceEmail"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 1 email"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReferencePhone"
								control={control}
								render={({ field }) => (
									<MaskInput
										{...field}
										id="secondaryWorkReferencePhone"
										name="secondaryWorkReferencePhone"
										label="Industry work reference 1 cell phone #"
										mask="phone"
										fullWidth
									/>
								)}
							/>
						</Grid>

						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReference2FirstName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 first name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReference2LastName"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 last name"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReference2Email"
								control={control}
								render={({ field }) => (
									<Input
										{...field}
										label="Industry work reference 2 email"
										fullWidth
									/>
								)}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<Controller
								name="secondaryWorkReference2Phone"
								control={control}
								render={({ field }) => (
									<MaskInput
										{...field}
										id="secondaryWorkReference2Phone"
										name="secondaryWorkReference2Phone"
										label="Industry work reference 2 cell phone #"
										mask="phone"
										fullWidth
									/>
								)}
							/>
						</Grid>
					</Grid>
				</AccordionDetails>
			</Accordion>

			{/* Preferences */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Preferences
			</Typography>
			<Box sx={{ mb: 2 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					Are you willing to be photographed on set for behind the
					scenes capture?
				</Typography>
				<Controller
					name="willingToBePhotographedOnSet"
					control={control}
					render={({ field }) => (
						<RadioButtons
							label=""
							name="willingToBePhotographedOnSet"
							options={[
								{ value: 'Yes', label: 'Yes' },
								{ value: 'No', label: 'No' },
							]}
							value={
								field.value === true
									? 'Yes'
									: field.value === false
									? 'No'
									: ''
							}
							onChange={(e: any) =>
								field.onChange(e.target.value === 'Yes')
							}
							row
						/>
					)}
				/>
			</Box>
			<Box sx={{ mb: 4 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					Dietary restrictions
				</Typography>
				<Controller
					name="dietaryRestrictions"
					control={control}
					render={({ field }) => (
						<Input
							{...field}
							multiline
							rows={4}
							fullWidth
							placeholder="Please describe any dietary restrictions..."
						/>
					)}
				/>
			</Box>
			<Grid container spacing={3} sx={{ mb: 4 }}>
				<Grid item xs={12} sm={6}>
					<Controller
						name="shirtSize"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								label="Shirt size"
								options={shirtSizeOptions}
								fullWidth
							/>
						)}
					/>
				</Grid>
				<Grid item xs={12} sm={6}>
					<Controller
						name="shoeSize"
						control={control}
						render={({ field }) => (
							<Select
								{...field}
								label="Shoe size"
								options={shoeSizeOptions}
								fullWidth
							/>
						)}
					/>
				</Grid>
			</Grid>

			{/* Certifications */}
			<Typography variant="h6" sx={{ mb: 2, color: 'text.label' }}>
				Certifications
			</Typography>
			<Box sx={{ mb: 4 }}>
				<Typography variant="body2" sx={{ mb: 1 }}>
					Enter certification (ex: CPR)
				</Typography>
				<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
					{formValues.certifications.map((cert, index) => (
						<Box
							key={index}
							sx={{
								display: 'flex',
								gap: 2,
								alignItems: 'center',
							}}
						>
							<Input
								value={cert}
								onChange={(e) => {
									const newCerts = [
										...formValues.certifications,
									];
									newCerts[index] = e.target.value;
									setValue('certifications', newCerts, {
										shouldDirty: true,
									});
								}}
								fullWidth
								placeholder="Enter certification"
							/>
							<Button
								variant="text"
								color="error"
								onClick={() => {
									const newCerts =
										formValues.certifications.filter(
											(_, i) => i !== index
										);
									setValue('certifications', newCerts, {
										shouldDirty: true,
									});
								}}
							>
								Remove
							</Button>
						</Box>
					))}
					<Button
						variant="text"
						color="primary"
						onClick={() => {
							setValue(
								'certifications',
								[...formValues.certifications, ''],
								{
									shouldDirty: true,
								}
							);
						}}
						sx={{ alignSelf: 'flex-start' }}
					>
						+ ADD CERTIFICATION
					</Button>
				</Box>
			</Box>
		</Box>
	);
}
